import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { clsx } from 'clsx';

import { css } from '@emotion/react';
import {
  inputAdornmentClasses,
  inputBaseClasses as muiInputBaseClasses,
  MenuItem,
  outlinedInputClasses as muiOutlinedInputClasses,
  Select as MuiSelect,
  selectClasses as muiSelectClasses,
  styled,
} from '@mui/material';
import { useTranslation } from '@kc-console/core';
import { IcChevronDownSmallRegular } from '@kep/kcds-icons';

import { TextInput } from '../../components/TextInput';
import {
  comparePrimarySubtextsSearchMatch,
  getPrimarySubtextsSortLabel,
  observeResizeEvent,
  scorePrimarySubtextsForItem,
  sortMetaPrimarySubtextsForItem,
  sortSectionedRowsByLeafSearchMatch,
  useCascadeRef,
  useCascadeState,
  useElementIsEllipsis,
  useHotRef,
} from '../../utils';
import { DraggableDropdownMenu2 } from '../DraggableDropdownMenu2';
import { DropdownMenu2, dropdownMenu2Classes } from '../DropdownMenu2';
import { MenuContainerToolbar } from '../MenuContainerToolbar';
import { Tooltip } from '../Tooltip';
import { SelectConfirmableButtons } from './SelectConfirmableButtons';
import { selectClasses } from './theme';

import type { ChangeEvent, MouseEvent, ReactElement, Ref } from 'react';
import type { ParameterAt } from '@kc-console/core';
import type { DraggableMenuOptionItemData } from '../DraggableDropdownMenu2';
import type {
  DropdownMenu2ApiRef,
  DropdownMenu2ItemData,
  DropdownMenu2OptionItemData,
} from '../DropdownMenu2';
import type {
  SelectItemOption,
  SelectOption,
  SelectOwnerState,
  SelectProps,
  SelectSectionOption,
  SelectValue,
} from './types';

const selectLeafPrimary = <OptionValue, ExtraOption extends Record<string, any>>(
  o: SelectItemOption<OptionValue, ExtraOption>,
) => o.text ?? '';

const selectLeafSubs = <OptionValue, ExtraOption extends Record<string, any>>(
  o: SelectItemOption<OptionValue, ExtraOption>,
) => o.subTexts;

const getSelectItemSearchMatchScore = <OptionValue, ExtraOption extends Record<string, any>>(
  option: SelectItemOption<OptionValue, ExtraOption>,
  searchKeyword: string,
) => scorePrimarySubtextsForItem(option, searchKeyword, selectLeafPrimary, selectLeafSubs);

/**
 * 필터링된 옵션을 검색 매칭 우선순위로 정렬한다. `text` 우선·`subTexts` 보조(3·4단계) 규칙은 MenuList와 동일.
 */
function sortFilteredSelectOptions<OptionValue, ExtraOption extends Record<string, any>>(
  options: SelectOption<OptionValue, ExtraOption>[],
  searchKeyword: string,
): SelectOption<OptionValue, ExtraOption>[] {
  const kw = searchKeyword.trim();
  if (!kw) return options;

  return sortSectionedRowsByLeafSearchMatch<
    SelectOption<OptionValue, ExtraOption>,
    SelectItemOption<OptionValue, ExtraOption>
  >({
    rows: options,
    searchKeyword,
    preserveStableOrderWhenSearchMetaTies: true,
    isSection: (row): row is SelectSectionOption<OptionValue, ExtraOption> =>
      row.type === 'section',
    getSectionLeaves: row => [...row.items],
    cloneSectionWithLeaves: (section, sortedLeaves) => ({
      ...section,
      items: sortedLeaves,
    }),
    compareLeaves: (a, b, keyword) =>
      comparePrimarySubtextsSearchMatch(a, b, keyword, selectLeafPrimary, selectLeafSubs, {
        preserveStableOrderWhenSearchMetaTies: true,
      }),
    getLeafSearchSortMeta: (leaf, keyword) =>
      sortMetaPrimarySubtextsForItem(leaf, keyword, selectLeafPrimary, selectLeafSubs),
    getSectionSortLabel: section => section.title,
    getLeafSortLabel: leaf =>
      getPrimarySubtextsSortLabel(selectLeafPrimary(leaf), selectLeafSubs(leaf)),
  });
}

export function Select<
  OptionValue = unknown,
  Multiple extends boolean = false,
  Value = SelectValue<OptionValue, Multiple>,
  ExtraOption extends Record<string, any> = {},
>(props: SelectProps<OptionValue, Multiple, Value, ExtraOption>) {
  const { t } = useTranslation();
  const {
    autoFocus,
    classes,
    className,
    confirmable,
    defaultValue,
    disabled,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    disablePortal,
    enableHighlight = true,
    enableReordering,
    DropdownMenuProps,
    endAdornment,
    error,
    expandable,
    filterOptions: filterOptionsProp,
    inputRef: inputRefProp,
    loading,
    multiple,
    name,
    noFilteredOptionsText = t('common:common.list.no_matching_items'),
    noOptionsText = t('common:common.list.no_selectable_items'),
    onChange: onChangeProp,
    onSearchInputChange: onSearchInputChangeProp,
    onOptionsReorder,
    options,
    placeholder,
    placeholderOnEmpty = true,
    readOnly,
    ref,
    renderFooter: renderFooterProp,
    renderInput: renderInputProp,
    renderValue: renderValueProp,
    renderTooltip: renderTooltipProp,
    searchable,
    searchInputPlaceholder = t('common:common.general.enter_search_term'),
    searchInputValue: searchInputValueProp,
    selectedType: selectedTypeProp,
    selectionControls,
    disableTooltipOnEllipsis = false,
    size,
    slotProps,
    slots,
    startAdornment,
    style,
    value: valueProp,
    open: openProp,
    onOpen,
    onClose,
    variant,
    autoWidth = 'outside',
  } = props;
  const { footer: FooterSlotComponent = SelectConfirmableButtons } = slots ?? {};
  const { footer: footerSlotProps, dropdownMenu: dropdownMenuSlotProps } = slotProps ?? {};

  const flattenedItemOptions = useMemo(() => flattenSelectOptions(options), [options]);

  const selectedType = multiple
    ? 'checkbox'
    : confirmable
      ? 'radio'
      : (selectedTypeProp ?? 'check');

  const rootRef = useCascadeRef<HTMLDivElement | null>(null, ref);
  const inputRef = useCascadeRef<HTMLInputElement | null>(null, inputRefProp);
  const dropdownMenuApiRef = useRef<DropdownMenu2ApiRef>(null);
  const generatedId = useId();
  const triggerId = `${generatedId}-trigger`;
  const listboxId = `${generatedId}-listbox`;
  const dialogId = `${generatedId}-dialog`;

  const focusDisplay = useCallback(() => {
    const rootElement = rootRef.current;
    const displayElement =
      (rootElement?.matches(`.${muiSelectClasses.select},[role="button"],[role="combobox"]`)
        ? rootElement
        : null) ??
      rootElement?.querySelector<HTMLElement>(`.${muiSelectClasses.select}`) ??
      rootElement?.querySelector<HTMLElement>('[role="button"],[role="combobox"]');

    if (displayElement) {
      displayElement.focus();
      return displayElement;
    }

    if (inputRef.current) {
      inputRef.current.focus();
      return inputRef.current;
    }

    return null;
  }, [inputRef, rootRef]);

  const [value, setValue] = useCascadeState<Value>({
    initialValue: defaultValue ?? ((multiple ? [] : undefined) as Value),
    value: valueProp,
    onChange: value => onChangeProp?.(value),
  });
  const [previewValue, setPreviewValue] = useState<Value>(value);
  const [previewOptions, setPreviewOptions] =
    useState<SelectOption<OptionValue, ExtraOption>[]>(options);

  const [open, setOpen] = useCascadeState({
    initialValue: false,
    value: openProp,
    onChange: value => (value ? onOpen?.() : onClose?.()),
  });
  const handleOpen = useCallback(() => setOpen(true), [setOpen]);
  const handleCloseWithCancel = useCallback(() => {
    setPreviewValue(value);
    setPreviewOptions(options);
    setOpen(false);
    focusDisplay();
  }, [value, options, setPreviewValue, setOpen, focusDisplay]);

  const [searchInputValue, onSearchInputChange] = useCascadeState({
    value: searchInputValueProp,
    onChange: onSearchInputChangeProp,
  });

  const [switchChecked, setSwitchChecked] = useState(false);

  const isValueEmpty = multiple
    ? Array.isArray(previewValue) && previewValue.length === 0
    : previewValue === null || previewValue === undefined || previewValue === '';

  const { ref: ellipsisRef, isEllipsis } = useElementIsEllipsis();

  useEffect(() => {
    const rootElement = rootRef.current;
    const displayElement =
      (rootElement?.matches(`.${muiSelectClasses.select},[role="button"],[role="combobox"]`)
        ? rootElement
        : null) ??
      rootElement?.querySelector<HTMLElement>(`.${muiSelectClasses.select}`) ??
      rootElement?.querySelector<HTMLElement>('[role="button"],[role="combobox"]');
    const target =
      placeholderOnEmpty && isValueEmpty
        ? displayElement?.querySelector<HTMLElement>(`.${selectClasses.placeholder}`)
        : displayElement;
    ellipsisRef(target ?? null);
  });

  const getIsAllOptionsSelected = () => {
    return (
      multiple &&
      Array.isArray(previewValue) &&
      filteredSelectableOptions.length > 0 &&
      filteredSelectableOptions.every(option => getIsOptionSelected(option))
    );
  };

  const getIsSomeOptionsSelected = () => {
    const selectedCount = filteredSelectableOptions.filter(option =>
      getIsOptionSelected(option),
    ).length;
    return (
      multiple &&
      Array.isArray(previewValue) &&
      selectedCount > 0 &&
      selectedCount < filteredSelectableOptions.length
    );
  };

  const handleChange = (value: Value) => {
    setPreviewValue(value);
    if (!confirmable) {
      setValue(value);
      if (!multiple) {
        setOpen(false);
        focusDisplay();
      }
    }
  };

  const handleChangeBySelect = (
    event: (Event & { target: { name: string; value: unknown } }) | ChangeEvent<HTMLInputElement>,
  ) => {
    const eventValue = event.target.value;
    const value = (
      multiple ? (Array.isArray(eventValue) ? eventValue : [eventValue]) : eventValue
    ) as Value;
    handleChange(value);
  };

  const handleChangeByOption = (option: SelectItemOption<OptionValue, ExtraOption>) => {
    const selected = getIsOptionSelected(option);

    let nextValue: Value;
    if (multiple) {
      const prevValue = Array.isArray(previewValue) ? previewValue : [];
      if (selected) {
        nextValue = prevValue.filter(v => v !== option.value) as Value;
      } else {
        nextValue = [...prevValue, option.value] as Value;
      }
    } else {
      nextValue = option.value as SelectValue<OptionValue, Multiple> as Value;
    }
    handleChange(nextValue);
  };

  const handleChangeBySelectAllOption = (
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    const isFilteringActive = !!searchInputValue || switchChecked;
    const filteredSelectableValues = filteredSelectableOptions.map(option => option.value);

    let nextValue: Value;
    if (!isFilteringActive) {
      nextValue = (checked ? filteredSelectableValues : []) as Value;
    } else if (checked) {
      const prevValue = Array.isArray(previewValue) ? previewValue : [];
      nextValue = [...new Set([...prevValue, ...filteredSelectableValues])] as Value;
    } else {
      const prevValue = Array.isArray(previewValue) ? previewValue : [];
      const valueSet = new Set(filteredSelectableValues);
      nextValue = prevValue.filter(v => !valueSet.has(v)) as Value;
    }
    handleChange(nextValue);
  };

  const handleChangeByConfirm = useCallback(() => {
    setValue(previewValue);

    // 순서가 변경되었다면 onOptionsReorder 호출
    if (onOptionsReorder && JSON.stringify(options) !== JSON.stringify(previewOptions)) {
      // 섹션이 아닌 아이템들만 추출하여 콜백 호출
      const reorderedOptions = previewOptions.filter(
        (option): option is SelectItemOption<OptionValue, ExtraOption> =>
          !option.type || option.type === 'item',
      );
      onOptionsReorder(reorderedOptions);
    }

    setOpen(false);
    focusDisplay();
  }, [previewValue, previewOptions, options, onOptionsReorder, setOpen, setValue, focusDisplay]);

  const getIsOptionSelected = (option: SelectItemOption<OptionValue, ExtraOption>): boolean => {
    const optionValue = option.value;
    return multiple
      ? Array.isArray(previewValue)
        ? previewValue.some(v => v === optionValue)
        : false
      : previewValue === (optionValue as any);
  };

  const filterOption = useCallback(
    (
      option: SelectItemOption<OptionValue, ExtraOption>,
      context: { searchInputValue?: string; switchChecked?: boolean },
    ): boolean => {
      const { searchInputValue = '', switchChecked = false } = context;

      if (switchChecked && option.disabled) {
        return false;
      }

      const kw = searchInputValue;
      if (!kw.trim()) {
        return true;
      }

      const priority = getSelectItemSearchMatchScore(option, kw);
      return Number.isFinite(priority);
    },
    [],
  );

  const filterOptions = useCallback(
    (
      options: SelectOption<OptionValue, ExtraOption>[],
      context: { searchInputValue?: string; switchChecked?: boolean },
    ) => {
      if (filterOptionsProp) {
        return filterOptionsProp(options, context);
      }

      const { searchInputValue = '', switchChecked = false } = context;

      if (!searchInputValue && !switchChecked) return options;

      const filtered = options
        .map((item): SelectOption<OptionValue, ExtraOption> | null => {
          if (item.type === 'section') {
            const filteredSectionItems = item.items?.filter(item => filterOption(item, context));
            if (!filteredSectionItems?.length) return null;
            return { ...item, items: filteredSectionItems } as SelectSectionOption<
              OptionValue,
              ExtraOption
            >;
          }
          return filterOption(item, context) ? item : null;
        })
        .filter(item => !!item);

      return searchInputValue.trim()
        ? sortFilteredSelectOptions(filtered, searchInputValue)
        : filtered;
    },
    [filterOptionsProp, filterOption],
  );

  const reorderSourceOptions: SelectOption<OptionValue, ExtraOption>[] = enableReordering
    ? previewOptions
    : options;

  const filteredOptions = useMemo(
    () => filterOptions(reorderSourceOptions, { searchInputValue, switchChecked }),
    [reorderSourceOptions, searchInputValue, switchChecked, filterOptions],
  );

  const flattenedFilteredItemOptions = useMemo(
    () => flattenSelectOptions(filteredOptions),
    [filteredOptions],
  );

  const filteredSelectableOptions = useMemo(
    () => flattenedFilteredItemOptions.filter(option => !option.disabled),
    [flattenedFilteredItemOptions],
  );

  const convertItemOption = (
    option: SelectItemOption<OptionValue, ExtraOption>,
  ): DropdownMenu2OptionItemData => {
    const selected = getIsOptionSelected(option);

    return {
      className: classes?.menuItem,
      disabled: option.disabled,
      endBadge: option.endBadge,
      icon: option.icon,
      onClick: () => handleChangeByOption(option),
      selected: selected,
      showDividerAbove: option.showDividerAbove,
      startBadge: option.startBadge,
      subTexts: option.subTexts,
      suffix: option.suffix,
      text: option.text,
      tooltip: option.tooltip,
      truncateSubTextIndex: option.truncateSubTextIndex,
    };
  };

  // SelectItemOption을 DraggableMenuOptionItemData로 변환하는 함수
  const convertToDraggableItem = (
    option: SelectItemOption<OptionValue, ExtraOption>,
  ): DraggableMenuOptionItemData => {
    const baseItem = convertItemOption(option);
    return {
      ...baseItem,
      id: `option-${String(option.value)}`,
      draggable: true,
    } as DraggableMenuOptionItemData;
  };

  const menuItems: DropdownMenu2ItemData[] = filteredOptions.map(
    (option): DropdownMenu2ItemData => {
      if (option.type === 'section') {
        return {
          type: 'section',
          title: option.title,
          items: option.items?.map(convertItemOption),
        };
      }
      return convertItemOption(option);
    },
  );

  // 드래그 가능한 메뉴 아이템들 (섹션이 아닌 아이템만)
  const draggableMenuItems: DraggableMenuOptionItemData[] = enableReordering
    ? filteredOptions
        .filter(
          (option): option is SelectItemOption<OptionValue, ExtraOption> =>
            !option.type || option.type === 'item',
        )
        .map(option => convertToDraggableItem(option))
    : [];

  // 옵션 순서 변경 핸들러
  const handleItemsReorder = useCallback(
    (reorderedItems: readonly DraggableMenuOptionItemData[]) => {
      if (!onOptionsReorder) return;

      const itemOptionMap = new Map<string, SelectItemOption<OptionValue, ExtraOption>>();
      reorderSourceOptions.forEach(option => {
        if (!option.type || option.type === 'item') {
          itemOptionMap.set(`option-${String(option.value)}`, option);
        }
      });

      const reorderedOptions = reorderedItems
        .map(item => itemOptionMap.get(item.id))
        .filter((option): option is SelectItemOption<OptionValue, ExtraOption> => Boolean(option));

      if (confirmable) {
        // confirmable일 때는 previewOptions를 업데이트 (간단하게 reorderedOptions를 사용)
        setPreviewOptions(reorderedOptions);
      } else {
        // confirmable이 아닐 때는 즉시 콜백 호출
        onOptionsReorder(reorderedOptions);
      }
    },
    [onOptionsReorder, reorderSourceOptions, confirmable],
  );

  const renderValue = useCallback(() => {
    if (placeholderOnEmpty && isValueEmpty) {
      return <SelectPlaceholder className={classes?.placeholder}>{placeholder}</SelectPlaceholder>;
    }
    if (renderValueProp) {
      return renderValueProp(previewValue);
    }
    return Array.isArray(previewValue)
      ? previewValue
          .map(value => flattenedItemOptions.find(o => o.value === value)?.text ?? value)
          .join(', ')
      : String(
          flattenedItemOptions.find(o => (o.value as any) === previewValue)?.text ?? previewValue,
        );
  }, [
    previewValue,
    renderValueProp,
    isValueEmpty,
    placeholder,
    placeholderOnEmpty,
    flattenedItemOptions,
    classes?.placeholder,
  ]);

  const renderTooltip = useCallback(() => {
    if (renderTooltipProp) {
      return renderTooltipProp(previewValue, { isEllipsis });
    }
    if (!disableTooltipOnEllipsis && isEllipsis) {
      // 값이 비어 있을 때 renderValue()는 회색(text/weakest)인 SelectPlaceholder 엘리먼트를
      // 반환하는데, 이를 그대로 tooltip 내용으로 쓰면 tooltip 안에서도 회색으로 노출됩니다.
      // placeholder 문자열만 반환해 tooltip 기본 텍스트 색상을 사용하도록 합니다.
      if (placeholderOnEmpty && isValueEmpty) {
        return placeholder;
      }
      return renderValue();
    }
  }, [
    renderTooltipProp,
    previewValue,
    isEllipsis,
    renderValue,
    disableTooltipOnEllipsis,
    placeholderOnEmpty,
    isValueEmpty,
    placeholder,
  ]);

  const handleMouseDown = (event: MouseEvent) => {
    if (event.button !== 0 || disabled || readOnly) {
      return;
    }

    if (event.target instanceof Element && event.target.closest('[data-select-no-focus]')) {
      return;
    }

    event.preventDefault();
    focusDisplay();

    if (!open) {
      handleOpen();
    } else {
      handleCloseWithCancel();
    }
  };

  const handleKeyDownCapture = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (disabled || readOnly) {
        return;
      }

      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (!open) {
        handleOpen();
        return;
      }

      dropdownMenuApiRef.current?.focusTrigger();

      if (event.key === 'ArrowDown') {
        dropdownMenuApiRef.current?.navigateDown();
        return;
      }

      dropdownMenuApiRef.current?.navigateUp();
    },
    [disabled, handleOpen, open, readOnly],
  );

  const header = !!selectionControls && (
    <MenuContainerToolbar
      isSearched={!!searchInputValue}
      showSelectAllCheckbox={multiple && selectionControls.showSelectAllCheckbox}
      selectAllChecked={getIsAllOptionsSelected()}
      selectAllIndeterminate={getIsSomeOptionsSelected()}
      onSelectAllChange={handleChangeBySelectAllOption}
      selectedCount={
        selectionControls.showSelectedCount && multiple
          ? Array.isArray(previewValue)
            ? previewValue.length
            : 0
          : undefined
      }
      showSwitch={selectionControls.showSwitch}
      switchChecked={switchChecked}
      onSwitchChange={(e, checked) => setSwitchChecked(checked)}
      switchLabel={selectionControls.switchLabel}
    />
  );

  const renderFooter: Exclude<
    SelectProps<OptionValue, Multiple, Value, ExtraOption>['renderFooter'],
    undefined
  > = useCallback(
    params => {
      if (renderFooterProp) return renderFooterProp(params);
      if (!confirmable) return null;
      return (
        <FooterSlotComponent {...params} disabledApply={params.isValueEmpty} {...footerSlotProps} />
      );
    },
    [renderFooterProp, confirmable, FooterSlotComponent, footerSlotProps],
  );

  const footer = useMemo(
    () =>
      renderFooter({
        filteredOptions: options /* TODO footer에는 filteredOptions 못넘겨줌 */,
        isValueEmpty,
        onApply: handleChangeByConfirm,
        onCancel: handleCloseWithCancel,
        onClose: () => setOpen(false),
        options,
        previewValue,
        setPreviewValue,
        setValue,
        value,
      }),
    [
      renderFooter,
      isValueEmpty,
      handleChangeByConfirm,
      handleCloseWithCancel,
      setOpen,
      options,
      previewValue,
      setPreviewValue,
      setValue,
      value,
    ],
  );

  const renderInput = useHotRef(renderInputProp);
  type RenderInputParams = ParameterAt<
    Exclude<SelectProps<OptionValue, Multiple, Value, ExtraOption>['renderInput'], undefined>
  >;

  /**
   * NOTE (@walter-24) MuiSelect의 input으로 전달하기 위한 컴포넌트, MuiSelect 내부에서 value/onChange/onMouseDown 등의 로직 관련 props를 children injection으로 주입하는 방식에 대응하기 위해 컴포넌트로 구현합니다.
   *
   * 만약 `<SelectRoot input={renderInput({ ... })} />` 방식으로 `renderInput`을 바로 호출하여 전달한다면, value/onChange/onMouseDown 등의 props가 renderInput에는 전달되지 않습니다.
   */
  const InputRenderer = useCallback(
    ((props: RenderInputParams) => {
      if (renderInput.current) {
        return renderInput.current(props);
      }

      const {
        'aria-controls': _ariaControls,
        'aria-disabled': _ariaDisabled,
        'aria-expanded': _ariaExpanded,
        'aria-haspopup': _ariaHasPopup,
        'aria-invalid': _ariaInvalid,
        'aria-label': _ariaLabel,
        'aria-labelledby': _ariaLabelledBy,
        'aria-readonly': _ariaReadOnly,
        autoFocus,
        endAdornment,
        id: _id,
        isValueEmpty: _1,
        name,
        onChange,
        onOpen: _3,
        placeholderOnEmpty: _2,
        ref,
        role: _role,
        startAdornment,
        tabIndex: _tabIndex,
        variant,
        ...others
      } = props;

      return (
        <TextInput
          autoFocus={autoFocus}
          ref={ref as Ref<HTMLDivElement>}
          name={name}
          disabledClearable
          disabledOverflow
          endAdornment={endAdornment}
          onChange={event => onChange(event as ChangeEvent<HTMLInputElement>)}
          startAdornment={startAdornment}
          variant={variant}
          {...others}
        />
      );
    }) as (params: Omit<RenderInputParams, 'value' | 'onChange'>) => ReactElement,
    [renderInput],
  );

  const [inputWidth, setInputWidth] = useState<number | null>(null);

  const ownerState = {
    ...props,
    autoWidth,
    placeholderOnEmpty,
    searchInputPlaceholder,
    disableTooltipOnEllipsis,
    inputWidth,
  } as SelectOwnerState;

  useEffect(() => {
    if (!open) return;
    setPreviewValue(value);
  }, [open, value]);

  useEffect(() => {
    if (open) {
      onSearchInputChange('');
    }
  }, [open, onSearchInputChange]);

  useEffect(() => {
    if (!open) {
      setPreviewValue(value);
    }
  }, [open, value]);

  useEffect(() => {
    setPreviewOptions(options);
  }, [options]);

  useEffect(() => {
    return observeResizeEvent(rootRef, () => {
      setInputWidth(rootRef.current?.clientWidth ?? null);
    });
  }, [rootRef]);

  const hasRichPopup = searchable || !!header || !!footer || !!expandable || !!enableReordering;
  const popupId = hasRichPopup ? dialogId : listboxId;
  const popupRole = hasRichPopup ? 'dialog' : 'listbox';

  const select = (
    <SelectRoot
      autoFocus={autoFocus}
      inputRef={inputRef}
      IconComponent={IcChevronDownSmallRegular}
      MenuProps={{ open: false, style: { display: 'none' } }}
      className={clsx(className, classes?.root)}
      classes={{
        icon: selectClasses.icon,
      }}
      disabled={disabled}
      displayEmpty
      input={
        <InputRenderer
          disabled={disabled}
          endAdornment={endAdornment}
          error={error}
          id={triggerId}
          isValueEmpty={isValueEmpty}
          key={name}
          loading={loading}
          multiple={multiple}
          name={name}
          onOpen={handleOpen}
          open={open}
          placeholder={placeholder}
          placeholderOnEmpty={placeholderOnEmpty}
          readOnly={readOnly}
          role="combobox"
          size={size}
          startAdornment={startAdornment}
          tabIndex={disabled ? -1 : 0}
          variant={variant}
          aria-controls={open ? popupId : undefined}
          aria-disabled={disabled || undefined}
          aria-expanded={open}
          aria-haspopup={popupRole}
          aria-invalid={error || undefined}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-readonly={readOnly || undefined}
        />
      }
      SelectDisplayProps={{
        id: triggerId,
        'aria-controls': open ? popupId : undefined,
        'aria-expanded': open,
        'aria-haspopup': popupRole,
        'aria-label': ariaLabel,
        'aria-labelledby': ariaLabelledBy,
        'aria-readonly': readOnly || undefined,
      }}
      multiple={multiple}
      open={open}
      ownerState={ownerState}
      readOnly={readOnly}
      ref={rootRef}
      renderValue={renderValue}
      style={style}
      value={value}
      onChange={handleChangeBySelect}
      onClose={handleCloseWithCancel}
      onKeyDownCapture={handleKeyDownCapture}
      onMouseDown={handleMouseDown}
      onOpen={handleOpen}
    >
      <MenuItem disabled></MenuItem>
      {value != null && <MenuItem value={String(value)}></MenuItem>}
    </SelectRoot>
  );

  const { slotProps: dropdownMenuPropsSlotProps, ...dropdownMenu2Props } = DropdownMenuProps ?? {};
  const { slotProps: dropdownMenuSlotSlotProps, ...dropdownMenuSlotRootProps } =
    dropdownMenuSlotProps ?? {};
  const dropdownMenuSemanticSlotProps = {
    menuContainer: {
      ...dropdownMenuPropsSlotProps?.menuContainer,
      ...dropdownMenuSlotSlotProps?.menuContainer,
      id: hasRichPopup ? dialogId : undefined,
      role: hasRichPopup ? ('dialog' as const) : ('presentation' as const),
      'aria-label': hasRichPopup ? ariaLabel : undefined,
      'aria-labelledby': hasRichPopup ? ariaLabelledBy : undefined,
    },
    menuList: {
      ...dropdownMenuPropsSlotProps?.menuList,
      ...dropdownMenuSlotSlotProps?.menuList,
      id: listboxId,
      role: 'listbox' as const,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-multiselectable': multiple || undefined,
    },
    paper: {
      ...dropdownMenuPropsSlotProps?.paper,
      ...dropdownMenuSlotSlotProps?.paper,
    },
  };

  const menu = enableReordering ? (
    <DraggableDropdownMenu2
      apiRef={dropdownMenuApiRef}
      anchorEl={rootRef.current}
      autoFocusItem={!searchable}
      closeOnOptionClick={false}
      disablePortal={disablePortal}
      enableHighlight={enableHighlight}
      expandable={expandable}
      filterItems={keepData}
      header={header}
      searchable={searchable}
      searchPlaceholder={searchInputPlaceholder}
      searchText={searchInputValue}
      onSearchTextChange={onSearchInputChange}
      footer={footer}
      noFilteredOptionsText={noFilteredOptionsText}
      noOptionsText={noOptionsText}
      onClose={handleCloseWithCancel}
      onItemsReorder={handleItemsReorder}
      open={open}
      selectedType={selectedType}
      {...dropdownMenu2Props}
      {...dropdownMenuSlotRootProps}
      className={clsx(classes?.menu, DropdownMenuProps?.className)}
      items={draggableMenuItems}
      slotProps={dropdownMenuSemanticSlotProps}
    />
  ) : (
    <SelectMenu
      apiRef={dropdownMenuApiRef}
      anchorEl={rootRef.current}
      autoFocusItem={!searchable}
      closeOnOptionClick={false}
      disablePortal={disablePortal}
      expandable={expandable}
      enableHighlight={enableHighlight}
      filterItems={keepData}
      header={header}
      searchable={searchable}
      searchPlaceholder={searchInputPlaceholder}
      searchText={searchInputValue}
      onSearchTextChange={onSearchInputChange}
      footer={footer}
      noFilteredOptionsText={noFilteredOptionsText}
      noOptionsText={noOptionsText}
      onClose={handleCloseWithCancel}
      open={open}
      ownerState={ownerState}
      selectedType={selectedType}
      {...dropdownMenu2Props}
      {...dropdownMenuSlotRootProps}
      className={clsx(classes?.menu, DropdownMenuProps?.className)}
      items={menuItems}
      slotProps={dropdownMenuSemanticSlotProps}
    />
  );

  const tooltip = renderTooltip();

  return (
    <>
      <Tooltip message={tooltip} mountOnEmptyMessage>
        {select}
      </Tooltip>
      {menu}
    </>
  );
}

const SelectRoot = styled(MuiSelect, {
  target: selectClasses.root,
})<{ ownerState: SelectOwnerState }>`
  ${({ ownerState }) => {
    const { block, size } = ownerState;

    return css`
      display: ${block ? 'flex' : 'inline-flex'};
      ${block &&
      css`
        width: 100%;
        min-width: 0;
        max-width: 100%;
      `}
      cursor: pointer;

      /* disabled root의 입력 차단은 유지하고, placeholder/선택값 말줄임 Tooltip을 위해 표시 슬롯의 hover만 허용합니다. */
      &.${muiInputBaseClasses.disabled} .${muiSelectClasses.select} {
        cursor: default;
        user-select: none;
        pointer-events: auto;
      }

      & .${muiSelectClasses.select}.${muiInputBaseClasses.input} {
        height: 20px;
        min-height: auto;
        line-height: 20px;
      }

      && .${muiSelectClasses.select}.${muiOutlinedInputClasses.input} {
        padding-right: 0;
      }

      & .${inputAdornmentClasses.positionEnd} {
        margin-right: ${size === 'sm' || size === 'md'
          ? 16 + 6 - 2
          : size === 'lg'
            ? 20 + 8 - 2
            : 16 + 6}px;
      }

      & .${muiSelectClasses.icon} {
        font-size: ${size === 'lg' ? 20 : 16}px;
      }
    `;
  }}
`;

const SelectMenu = styled(DropdownMenu2, {
  target: selectClasses.menu,
})<{ ownerState: SelectOwnerState }>`
  .${dropdownMenu2Classes.paper} {
    max-height: ${({ ownerState: { maxHeight = 300 } }) =>
      typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight};
    ${({ ownerState: { autoWidth, inputWidth } }) => {
      if (autoWidth === 'as-content') {
        return css``;
      } else if (autoWidth === 'inside' || autoWidth === 'as-input' || autoWidth === 'outside') {
        return css`
          ${autoWidth === 'inside' && inputWidth ? `max-width: ${inputWidth}px` : ''};
          ${autoWidth === 'as-input' && inputWidth ? `width: ${inputWidth}px` : ''};
          ${autoWidth === 'outside' && inputWidth ? `min-width: ${inputWidth}px` : ''};
        `;
      } else {
        return css`
          width: ${typeof autoWidth === 'number' ? `${autoWidth}px` : autoWidth};
        `;
      }
    }}
  }
`;

const SelectPlaceholder = styled('span', {
  target: selectClasses.placeholder,
})`
  display: inline-block;
  max-width: 100%;
  min-height: 20px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  color: ${({ theme }) => theme.color['text/weakest/default']};

  .${selectClasses.root}.Mui-disabled & {
    -webkit-text-fill-color: ${({ theme }) => theme.color['text/disabled']};
  }
`;

const keepData = function <T>(data: T): T {
  return data;
};

function flattenSelectOptions<V, E extends Record<string, any>>(
  options: SelectOption<V, E>[],
): SelectItemOption<V, E>[] {
  return options.reduce(
    (acc, option) => {
      if (option.type === 'section' && option.items) {
        acc.push(...option.items);
      } else if (!option.type || option.type === 'item') {
        acc.push(option as SelectItemOption<V, E>);
      }
      return acc;
    },
    [] as SelectItemOption<V, E>[],
  );
}
